import React, { useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { EMAIL_ADDRESS_REGEX_WITH_TLD } from '@looop/graphql-scalars'

import { staticLinks } from '../../../../shared/linkBuilder'
import validations from '../../../../shared/validations'
import { ButtonPrimary } from 'front-end/src/components'
import Form from '../../components/common/form/Form'
import Input from '../../components/common/form/input/Input'
import Label from '../../components/common/form/label/Label'
import { notification } from '../../components/common/notifications/Notifications'
import SelectTimezone from '../../components/common/SelectTimezone'
import redirectToSubdomain from '../../receptionist/util/redirectToSubdomain'
import { IOrganisationInput, IUserInput } from './types'
import { useSignupMutation } from './SignUp.codegen'
import { useLoginPageQuery } from './login/LoginPage.codegen'
import { PageWithSingleAlert } from '../PageWithSingleAlert'
import dayjs from 'shared/dayjs'

import './Auth.less'
import IconArrowRight from 'front-end/src/components/common/icon/IconArrowRight'

enum steps {
  AUTH = 'AUTH',
  ORGANISATION = 'ORGANISATION',
  NAME = 'NAME'
}

const SignUp = ({ match }) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState(match.params.email)
  const [password, setPassword] = useState('')
  const [terms, setTerms] = useState(false)
  const [step, setStep] = useState(steps.AUTH)
  const [organisationName, setOrganisationName] = useState('')
  const [timezone, setTimezone] = useState(dayjs.tz.guess())

  const [signupMutation, { loading }] = useSignupMutation()
  const { data } = useLoginPageQuery()

  if (data?.organisation) {
    return <PageWithSingleAlert title={t`This subdomain is already taken`} />
  }

  const stepsRenderingMap = {
    [steps.AUTH]: (
      <Form
        onSubmit={() => {
          if (!email.match(EMAIL_ADDRESS_REGEX_WITH_TLD)) {
            notification.warning(t`Please insert valid email`)
            return
          }
          if (password.match(validations.passwordRegex)) {
            setStep(steps.NAME)
          } else {
            notification.warning(
              t`Please insert a valid password. Passwords must be at least 8 characters containing both letters and numbers.`
            )
          }
        }}
      >
        <h3>
          <Trans>
            Welcome to <strong>Looop</strong>!
          </Trans>
        </h3>

        <p>
          <Trans>
            Let's get you set up. Please choose an email address and password
            for your Looop account.
          </Trans>
        </p>

        <div className="form-row">
          <Label htmlFor="inputEmail">
            <Trans>Email</Trans>
          </Label>
          <Input
            type="email"
            id="inputEmail"
            placeholder="you@email.com"
            fullWidth
            onChange={(value) => {
              setEmail(value)
            }}
          />
        </div>
        <div className="form-row">
          <Label htmlFor="inputPassword">
            <Trans>Password</Trans>
          </Label>
          <Input
            type="password"
            id="inputPassword"
            placeholder="Password"
            fullWidth
            onChange={(value) => {
              setPassword(value)
            }}
          />
        </div>

        <div className="form-row checkbox">
          <label>
            <input
              type="checkbox"
              id="inputTerms"
              checked={terms}
              onChange={() => {
                setTerms(!terms)
              }}
            />
            <Trans>
              I agree to the
              <a
                href="https://www.looop.co/wp-content/uploads/2018/05/Looop-TCs.pdf" // TODO fix this link
                rel="noreferrer noopener"
                target="_blank"
              >
                terms & conditions
              </a>
            </Trans>
          </label>
        </div>
        <div className="form-row">
          <ButtonPrimary
            isFullWidth
            type="submit"
            disabled={!(email && password && terms)}
          >
            <Trans>Next</Trans> <span className="icon icon-arrow-right" />
          </ButtonPrimary>
        </div>
      </Form>
    ),
    [steps.NAME]: (
      <Form onSubmit={() => setStep(steps.ORGANISATION)}>
        <h3>
          <Trans>Choose a display name</Trans>
        </h3>
        <p>
          <Trans>
            This will be how people see you on Looop. Usually people choose
            their full name, or something easily recognisable.
          </Trans>
        </p>

        <div className="form-row">
          <Label htmlFor="nameInput">
            <Trans>Display name</Trans>
          </Label>
          <Input
            key={step}
            type="text"
            id="nameInput"
            fullWidth
            placeholder={t`Your name`}
            onChange={(value) => {
              setName(value)
            }}
          />
        </div>

        <div className="form-row">
          <ButtonPrimary isFullWidth type="submit">
            <Trans>Next</Trans> <span className="icon icon-arrow-right" />
          </ButtonPrimary>
        </div>
      </Form>
    ),
    [steps.ORGANISATION]: (
      <Form
        onSubmit={async () => {
          const userInput: IUserInput = {
            password,
            name,
            email
          }
          const organisationInput: IOrganisationInput = {
            name: organisationName,
            timezone
          }

          try {
            const { data } = await signupMutation({
              variables: { userInput, organisationInput }
            })
            redirectToSubdomain(data.signup.subdomain.name)
          } catch (e) {
            console.error(e)
            notification.error(t`Organisation cannot be created.`)
          }
        }}
      >
        <h3>
          <Trans>Name your account</Trans>
        </h3>
        <p className="form-text">
          <Trans>
            This is usually the name of your company or team. It will be visible
            to everyone you share your resources with.
          </Trans>
        </p>

        <div className="form-row">
          <Label htmlFor="accountNameInput">
            <Trans>Account name</Trans>
          </Label>
          <Input
            type="text"
            key={step}
            id="accountNameInput"
            placeholder={t`MyCorp International`}
            required
            fullWidth
            onChange={(value) => {
              setOrganisationName(value)
            }}
          />
          <label className="form-label">
            <Trans>Account timezone</Trans>
          </label>
          <SelectTimezone
            value={timezone}
            guess
            onChange={(tz) => {
              setTimezone(tz as string)
            }}
          />
        </div>

        <div className="form-row">
          <ButtonPrimary
            isFullWidth
            type="submit"
            isLoading={loading}
            disabled={loading}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Next</Trans>
          </ButtonPrimary>
        </div>
      </Form>
    )
  }

  return (
    <div className="Auth">
      <div className="Auth__container">{stepsRenderingMap[step]}</div>
      {step === steps.AUTH && (
        <p className="bottomLink">
          <Trans>
            If you already have an account or are
            <br />
            responding to an invitation, you can{' '}
            <a href={`/${staticLinks.login}`}>sign in here</a>
          </Trans>
        </p>
      )}
    </div>
  )
}

export default SignUp
